<template>
    <div class="min-h-screen w-full flex flex-col bg-gray-50">
        <!-- Content -->
        <div class="container mx-auto px-4 md:px-6 mt-8 pb-16">
            <div v-if="error" class="flex items-center justify-center py-16">
                <div class="text-center max-w-md">
                    <div class="text-red-500 text-5xl mb-4">
                        <font-awesome-icon icon="exclamation-triangle" />
                    </div>
                    <h2 class="text-xl font-bold text-gray-900 mb-2">Profile Not Available</h2>
                    <p class="text-gray-600 mb-6">{{ error }}</p>
                    <router-link to="/dashboard"
                        class="inline-block bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-colors">
                        Go to Dashboard
                    </router-link>
                </div>
            </div>
            <div v-else class="grid grid-cols-1 lg:grid-cols-12 gap-6">
                <!-- Left profile card -->
                <aside class="lg:col-span-3">
                    <div class="bg-white shadow-sm rounded-2xl p-6 border border-gray-200">
                        <div v-if="isLoadingProfile" class="mb-2 flex items-center justify-center">
                            <div class="w-28 h-28 rounded-2xl bg-gray-200 animate-pulse"></div>
                        </div>
                        <div v-else class="mb-2 flex items-center justify-center relative">
                            <div
                                class="relative w-28 h-28 rounded-2xl bg-indigo-100 border-2 border-indigo-200 shadow flex items-center justify-center overflow-hidden">
                                <img v-if="profile.profilePictureUrl"
                                    :src="getProfilePicture(profile.profilePictureUrl)" alt="avatar"
                                    class="w-full h-full object-cover" @error="handleImageError">
                                <div v-else
                                    class="w-20 h-20 rounded-xl bg-white flex items-center justify-center text-4xl">😮
                                </div>
                                <label v-if="isOwnProfile" for="profile-picture-input"
                                    class="absolute right-0 top-0 bg-white rounded-2xl shadow w-8 h-8 flex flex-col justify-center items-center cursor-pointer hover:shadow-md"
                                    title="Update profile picture">
                                    <font-awesome-icon icon="camera" class="text-gray-700" />
                                </label>
                            </div>
                            <input v-if="isOwnProfile" id="profile-picture-input" type="file" class="hidden"
                                accept="image/jpeg,image/png,image/gif,image/webp" @change="onProfilePictureChange">
                        </div>
                        <div v-if="isLoadingProfile" class="text-center mt-4 animate-pulse">
                            <div class="h-4 bg-gray-200 rounded w-40 mx-auto mb-2"></div>
                            <div class="h-3 bg-gray-200 rounded w-24 mx-auto mb-4"></div>
                            <div class="h-3 bg-gray-200 rounded w-56 mx-auto mb-2"></div>
                            <div class="h-3 bg-gray-200 rounded w-48 mx-auto mb-2"></div>
                        </div>
                        <div v-else class="text-center mt-4">
                            <h2 class="text-lg font-extrabold text-indigo-900">{{ profile.name || '—' }}</h2>
                            <p class="text-xs text-gray-500 mt-1">{{ profile.position }}</p>
                            <p class="text-sm text-gray-600 mt-3 leading-relaxed">
                                {{ profile.bio }}
                            </p>
                            <div class="mt-3 flex flex-col items-center gap-1 text-xs text-gray-600">
                                <div v-if="profile.phone" class="flex items-center gap-2">
                                    <font-awesome-icon icon="phone" class="text-emerald-600" />
                                    <span>{{ profile.phone }}</span>
                                </div>
                                <div v-if="profile.email" class="flex items-center gap-2">
                                    <span class="text-sky-600">@</span>
                                    <span class="truncate max-w-full">{{ profile.email }}</span>
                                </div>
                                <div class="flex items-center gap-2">
                                    <font-awesome-icon icon="building" class="text-indigo-600" />
                                    <span>{{ displayEntity }}</span>
                                </div>
                                <div class="flex items-center gap-2">
                                    <font-awesome-icon icon="university" class="text-amber-600" />
                                    <span>{{ displayCampus }}</span>
                                </div>
                            </div>
                            <div class="mt-4 flex items-center justify-center gap-2 px-2 text-xs sm:text-sm">
                                <a v-if="profile.url" :href="profile.url" target="_blank" rel="noopener"
                                    :title="profile.url"
                                    class="text-sky-600 hover:text-indigo-900 font-medium truncate block">
                                    {{ profile.url }}
                                </a>
                            </div>
                        </div>

                        <div v-if="isLoadingProfile"
                            class="mt-6 flex items-center justify-center space-x-3 animate-pulse">
                            <div class="px-12 py-2 rounded-full bg-gray-200"></div>
                            <div class="w-10 h-10 rounded-full bg-gray-200"></div>
                            <div class="w-10 h-10 rounded-full bg-gray-200"></div>
                        </div>
                        <div v-else class="mt-6 flex items-center justify-center space-x-3">
                            <button v-if="!isOwnProfile" @click="onClickFollow" :disabled="isFollowBusy"
                                class="px-4 py-2 rounded-full font-semibold shadow-sm transition"
                                :class="profile.viewerFollowStatus === 'FOLLOWING' ? 'bg-gray-100 hover:bg-gray-200 text-gray-800' : (profile.viewerFollowStatus === 'REQUESTED' ? 'bg-amber-100 hover:bg-amber-200 text-amber-900' : 'bg-indigo-600 hover:bg-indigo-700 text-white')">
                                {{ followActionLabel() }}
                            </button>
                            <button @click="copyProfileUrl" title="Copy profile link"
                                class="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 transition grid place-items-center">
                                <span class="text-gray-600">🔗</span>
                            </button>
                            <button v-if="isOwnProfile" title="Edit profile" @click="openEditModal"
                                class="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 transition grid place-items-center">
                                <span class="text-gray-600 inline-block rotate-90">✏️</span>
                            </button>
                        </div>

                        <div v-if="isLoadingProfile" class="mt-6 grid grid-cols-2 gap-4 text-center animate-pulse">
                            <div class="bg-gray-100 rounded-xl p-3">
                                <div class="h-3 bg-gray-200 rounded w-16 mx-auto mb-2"></div>
                                <div class="h-4 bg-gray-200 rounded w-10 mx-auto"></div>
                            </div>
                            <div class="bg-gray-100 rounded-xl p-3">
                                <div class="h-3 bg-gray-200 rounded w-16 mx-auto mb-2"></div>
                                <div class="h-4 bg-gray-200 rounded w-10 mx-auto"></div>
                            </div>
                        </div>
                        <div v-else class="mt-6 grid grid-cols-2 gap-4 text-center">
                            <button @click="openFollowersModal" type="button"
                                class="bg-gray-100 rounded-xl p-3 hover:bg-gray-200 transition text-center">
                                <p class="text-xs text-gray-500">Followers</p>
                                <p class="text-base font-bold text-indigo-900">{{ profile.followersCount }}</p>
                            </button>
                            <button @click="openFollowingModal" type="button"
                                class="bg-gray-100 rounded-xl p-3 hover:bg-gray-200 transition text-center">
                                <p class="text-xs text-gray-500">Following</p>
                                <p class="text-base font-bold text-indigo-900">{{ profile.followingCount }}</p>
                            </button>
                        </div>

                        <div v-if="isOwnProfile && !isLoadingProfile" class="mt-4">
                            <button @click="onTogglePrivacy"
                                class="w-full px-4 py-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-sm font-semibold text-gray-800 flex items-center justify-between">
                                <span>Private account</span>
                                <span class="text-xs"
                                    :class="profile.isPrivate ? 'text-amber-700' : 'text-emerald-700'">
                                    {{ profile.isPrivate ? 'ON' : 'OFF' }}
                                </span>
                            </button>
                            <p class="mt-2 text-xs text-gray-500">
                                If private, new followers must send a request you can accept or reject.
                            </p>

                            <button v-if="followRequestsCount > 0" @click="openFollowRequestsModal" type="button"
                                class="mt-4 w-full px-4 py-3 rounded-2xl border border-gray-200 bg-white hover:bg-gray-50 transition flex items-center justify-between">
                                <div class="text-left">
                                    <div class="text-sm font-extrabold text-indigo-900">Follow Requests</div>
                                    <div class="text-xs text-gray-500">Review and manage pending requests</div>
                                </div>
                                <div class="h-8 px-3 rounded-full bg-indigo-600 text-white text-sm font-bold grid place-items-center">
                                    {{ followRequestsCount }}
                                </div>
                            </button>
                        </div>

                        <div v-if="isLoadingProfile"
                            class="mt-6 flex items-center justify-center space-x-4 text-gray-500 animate-pulse">
                            <div class="w-5 h-5 bg-gray-200 rounded"></div>
                            <div class="w-5 h-5 bg-gray-200 rounded"></div>
                            <div class="w-5 h-5 bg-gray-200 rounded"></div>
                        </div>
                        <div v-else class="mt-6 flex items-center justify-center space-x-4 text-gray-500">
                            <a v-if="profile.website" :href="normalizeUrl(profile.website)" target="_blank"
                                rel="noopener" class="transition" title="Website">
                                <font-awesome-icon icon="globe" class="text-lg" :style="{ color: '#0EA5E9' }" />
                            </a>
                            <a v-if="profile.linkedin" :href="normalizeUrl(profile.linkedin)" target="_blank"
                                rel="noopener" class="transition" title="LinkedIn">
                                <font-awesome-icon :icon="['fab', 'linkedin']" class="text-lg"
                                    :style="{ color: '#0A66C2' }" />
                            </a>
                            <a v-if="profile.instagram" :href="normalizeUrl(profile.instagram)" target="_blank"
                                rel="noopener" class="transition" title="Instagram">
                                <font-awesome-icon :icon="['fab', 'instagram']" class="text-lg"
                                    :style="{ color: '#E4405F' }" />
                            </a>
                            <a v-if="profile.facebook" :href="normalizeUrl(profile.facebook)" target="_blank"
                                rel="noopener" class="transition" title="Facebook">
                                <font-awesome-icon :icon="['fab', 'facebook']" class="text-lg"
                                    :style="{ color: '#1877F2' }" />
                            </a>
                            <a v-if="profile.snapchat" :href="normalizeUrl(profile.snapchat)" target="_blank"
                                rel="noopener" class="transition" title="Snapchat">
                                <font-awesome-icon :icon="['fab', 'snapchat']" class="text-lg"
                                    :style="{ color: '#FFC107' }" />
                            </a>
                            <a v-if="profile.twitter" :href="normalizeUrl(profile.twitter)" target="_blank"
                                rel="noopener" class="transition" title="Twitter">
                                <font-awesome-icon :icon="['fab', 'twitter']" class="text-lg"
                                    :style="{ color: '#1DA1F2' }" />
                            </a>
                            <a v-if="profile.youtube" :href="normalizeUrl(profile.youtube)" target="_blank"
                                rel="noopener" class="transition" title="YouTube">
                                <font-awesome-icon :icon="['fab', 'youtube']" class="text-lg"
                                    :style="{ color: '#FF0000' }" />
                            </a>
                        </div>

                        <div class="mt-6 text-center text-xs text-gray-500">Member since {{
                            formatMemberSince(profile.createdAt) }}</div>
                    </div>
                </aside>

                <!-- Right main area -->
                <main class="lg:col-span-9">
                    <div class="bg-white rounded-2xl shadow-sm border border-gray-200 h-full flex flex-col">
                        <!-- Tabs -->
                        <div class="px-4 md:px-6 pt-6">
                            <div class="flex items-center space-x-4 overflow-x-auto pb-4">
                                <button v-for="tab in displayedTabs" :key="tab.key" @click="selectTab(tab.key)"
                                    class="relative whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold transition border"
                                    :class="selectedTab === tab.key ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300'">
                                    <span v-if="tab.icon" class="mr-2">{{ tab.icon }}</span>{{ tab.label }}
                                </button>
                            </div>
                        </div>

                        <!-- Tab contents -->
                        <div class="p-4 md:p-6 flex-1">
                            <!-- About / detailed description -->
                            <profile-about v-if="selectedTab === 'about'" :description="profile.detailedDescription"
                                :is-own-profile="isOwnProfile" @saved="getProfile" />
                            <!-- Posts -->
                            <profile-posts v-else-if="selectedTab === 'posts'"
                                :user-token="$route.params.token || token || ''" :is-own-profile="isOwnProfile"
                                :posts-refresh-key="postsRefreshKey" />
                            <!-- Think Tank -->
                            <think-tank v-else-if="selectedTab === 'think-tank'"
                                :token="$route.params.token || token || ''" :user-name="profile.name || ''" />
                            <!-- Engagement -->
                            <engagement v-else-if="selectedTab === 'engagement'"
                                :token="$route.params.token || token || ''" :user-name="profile.name || ''" />
                            <!-- Student Life -->
                            <student-life v-else-if="selectedTab === 'student-life'"
                                :token="$route.params.token || token || ''" :user-name="profile.name || ''" />
                            <!-- Change Password -->
                            <change-password v-else-if="selectedTab === 'change-password' && isOwnProfile" />
                        </div>
                    </div>
                </main>
            </div>
        </div>

        <!-- Edit Profile Modal -->
        <base-modal v-if="showEditProfileModal" title="Edit profile" @close="closeEditModal">
            <div class="p-5 space-y-4">
                <div class="space-y-3">
                    <div class="bg-white rounded-lg border border-gray-200 p-3 flex items-center gap-3">
                        <font-awesome-icon icon="phone" class="text-emerald-600" />
                        <input type="text" v-model.trim="editForm.phone_number" placeholder="Phone number"
                            class="flex-1 border-0 outline-none focus:ring-0" />
                    </div>
                    <div class="bg-white rounded-lg border border-gray-200 p-3">
                        <div class="flex items-start gap-3">
                            <font-awesome-icon icon="user" class="text-indigo-600 mt-0.5" />
                            <textarea v-model.trim="editForm.bio" rows="3" placeholder="Short bio"
                                class="flex-1 border-0 outline-none focus:ring-0"></textarea>
                        </div>
                    </div>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div class="bg-white rounded-lg border border-gray-200 p-3 flex items-center gap-3">
                            <font-awesome-icon icon="globe" class="text-sky-500" />
                            <input type="text" v-model.trim="editForm.website" placeholder="Website"
                                class="flex-1 border-0 outline-none focus:ring-0" />
                        </div>
                        <div class="bg-white rounded-lg border border-gray-200 p-3 flex items-center gap-3">
                            <font-awesome-icon :icon="['fab', 'linkedin']" class="" :style="{ color: '#0A66C2' }" />
                            <input type="text" v-model.trim="editForm.linkedin" placeholder="LinkedIn"
                                class="flex-1 border-0 outline-none focus:ring-0" />
                        </div>
                        <div class="bg-white rounded-lg border border-gray-200 p-3 flex items-center gap-3">
                            <font-awesome-icon :icon="['fab', 'instagram']" :style="{ color: '#E4405F' }" />
                            <input type="text" v-model.trim="editForm.instagram" placeholder="Instagram"
                                class="flex-1 border-0 outline-none focus:ring-0" />
                        </div>
                        <div class="bg-white rounded-lg border border-gray-200 p-3 flex items-center gap-3">
                            <font-awesome-icon :icon="['fab', 'facebook']" :style="{ color: '#1877F2' }" />
                            <input type="text" v-model.trim="editForm.facebook" placeholder="Facebook"
                                class="flex-1 border-0 outline-none focus:ring-0" />
                        </div>
                        <div class="bg-white rounded-lg border border-gray-200 p-3 flex items-center gap-3">
                            <font-awesome-icon :icon="['fab', 'snapchat']" :style="{ color: '#FFC107' }" />
                            <input type="text" v-model.trim="editForm.snapchat" placeholder="Snapchat"
                                class="flex-1 border-0 outline-none focus:ring-0" />
                        </div>
                        <div class="bg-white rounded-lg border border-gray-200 p-3 flex items-center gap-3">
                            <font-awesome-icon :icon="['fab', 'twitter']" :style="{ color: '#1DA1F2' }" />
                            <input type="text" v-model.trim="editForm.twitter" placeholder="Twitter"
                                class="flex-1 border-0 outline-none focus:ring-0" />
                        </div>
                        <div
                            class="bg-white rounded-lg border border-gray-200 p-3 flex items-center gap-3 md:col-span-2">
                            <font-awesome-icon :icon="['fab', 'youtube']" :style="{ color: '#FF0000' }" />
                            <input type="text" v-model.trim="editForm.youtube" placeholder="YouTube"
                                class="flex-1 border-0 outline-none focus:ring-0" />
                        </div>
                    </div>
                </div>
                <div class="flex justify-end gap-2 pt-1">
                    <button @click="closeEditModal"
                        class="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700">Cancel</button>
                    <button @click="submitEditProfile"
                        class="px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white">Save</button>
                </div>
            </div>
        </base-modal>

        <!-- Followers / Following Modal -->
        <base-modal v-if="showFollowListModal" :title="followListTitle" @close="closeFollowListModal">
            <div class="p-4">
                <div class="mb-3">
                    <input v-model.trim="followListSearch" @input="onFollowListSearchInput" type="text"
                        placeholder="Search by name, email, or position…"
                        class="w-full px-3 py-2 rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-0" />
                </div>

                <div v-if="followListLoadError" class="mb-3 text-sm text-red-600">
                    {{ followListLoadError }}
                </div>

                <div class="border border-gray-200 rounded-xl overflow-hidden">
                    <div @scroll="onFollowListScroll" style="max-height: 440px;" class="bg-white overflow-y-auto">
                        <div v-if="followListItems && followListItems.length === 0 && isLoadingFollowList"
                            class="p-4 text-sm text-gray-500">
                            Loading…
                        </div>

                        <div v-for="u in followListItems" :key="u.id"
                            class="p-4 border-b border-gray-100 flex items-center gap-3">
                            <router-link v-if="u.token" :to="'/profile/' + u.token" @click="closeFollowListModal"
                                class="flex items-center gap-3 min-w-0 flex-1">
                                <div
                                    class="w-10 h-10 rounded-xl bg-gray-100 overflow-hidden flex items-center justify-center">
                                    <img v-if="u.profile_picture_url" :src="getProfilePicture(u.profile_picture_url)"
                                        class="w-full h-full object-cover" alt="avatar" />
                                    <span v-else class="text-gray-500">🙂</span>
                                </div>
                                <div class="min-w-0">
                                    <div class="font-semibold text-gray-900 truncate hover:text-indigo-700 transition">
                                        {{ u.name }}</div>
                                    <div class="text-xs text-gray-500 truncate hover:text-indigo-700 transition">{{
                                        u.email || '' }}</div>
                                </div>
                            </router-link>
                            <div v-else class="flex items-center gap-3 min-w-0 flex-1">
                                <div
                                    class="w-10 h-10 rounded-xl bg-gray-100 overflow-hidden flex items-center justify-center">
                                    <span class="text-gray-500">🙂</span>
                                </div>
                                <div class="min-w-0">
                                    <div class="font-semibold text-gray-900 truncate">{{ u.name }}</div>
                                    <div class="text-xs text-gray-500 truncate">{{ u.email || '' }}</div>
                                </div>
                            </div>
                            <div class="flex items-center gap-2">
                                <button v-if="u.viewer_follow_status !== 'SELF'" @click="onClickListFollow(u)"
                                    :disabled="isBusyForToken(u.token)"
                                    class="px-3 py-1.5 rounded-lg text-sm font-semibold border transition"
                                    :class="u.viewer_follow_status === 'FOLLOWING' ? 'bg-gray-100 hover:bg-gray-200 text-gray-800 border-gray-200' : (u.viewer_follow_status === 'REQUESTED' ? 'bg-amber-100 hover:bg-amber-200 text-amber-900 border-amber-200' : 'bg-indigo-600 hover:bg-indigo-700 text-white border-indigo-600')">
                                    {{ listFollowLabel(u) }}
                                </button>

                                <button v-if="isOwnProfile && followListMode === 'followers'"
                                    @click="onRemoveFollower(u)" :disabled="isBusyForToken(u.token)"
                                    class="px-3 py-1.5 rounded-lg text-sm font-semibold bg-white border border-gray-200 hover:bg-gray-50 text-gray-700">
                                    Remove
                                </button>
                            </div>
                        </div>

                        <div v-if="isLoadingFollowList && followListItems && followListItems.length > 0"
                            class="p-4 text-sm text-gray-500">
                            Loading more…
                        </div>

                        <div v-if="!isLoadingFollowList && !followListHasMore && followListItems && followListItems.length > 0"
                            class="p-4 text-xs text-gray-400 text-center">
                            You’ve reached the end.
                        </div>

                        <div v-if="!isLoadingFollowList && (!followListItems || followListItems.length === 0) && !followListLoadError"
                            class="p-4 text-sm text-gray-500">
                            No users to show.
                        </div>
                    </div>
                </div>
            </div>
        </base-modal>

        <!-- Follow Requests Modal -->
        <base-modal v-if="showFollowRequestsModal" title="Follow Requests" @close="closeFollowRequestsModal">
            <div class="p-4">
                <div class="border border-gray-200 rounded-xl overflow-hidden">
                    <div @scroll="onFollowRequestsScroll" style="max-height: 440px;" class="bg-white overflow-y-auto">
                        <div v-if="followRequests && followRequests.length === 0 && isLoadingRequests"
                            class="p-4 text-sm text-gray-500">
                            Loading…
                        </div>

                        <div v-for="req in followRequests" :key="req.id"
                            class="p-4 border-b border-gray-100 flex items-center justify-between gap-3">
                            <router-link v-if="req.follower && req.follower.token" :to="'/profile/' + req.follower.token"
                                @click="closeFollowRequestsModal"
                                class="flex items-center gap-3 min-w-0 flex-1">
                                <div class="w-10 h-10 rounded-xl bg-gray-100 overflow-hidden flex items-center justify-center">
                                    <img v-if="req.follower && req.follower.profile_picture_url"
                                        :src="getProfilePicture(req.follower.profile_picture_url)"
                                        class="w-full h-full object-cover" alt="avatar" />
                                    <span v-else class="text-gray-500">🙂</span>
                                </div>
                                <div class="min-w-0">
                                    <div class="font-semibold text-gray-900 truncate hover:text-indigo-700 transition">
                                        {{ req.follower && req.follower.name ? req.follower.name : 'Unknown user' }}
                                    </div>
                                    <div class="text-xs text-gray-500 truncate hover:text-indigo-700 transition">
                                        {{ req.follower && req.follower.email ? req.follower.email : '' }}
                                    </div>
                                </div>
                            </router-link>
                            <div v-else class="flex items-center gap-3 min-w-0 flex-1">
                                <div class="w-10 h-10 rounded-xl bg-gray-100 overflow-hidden flex items-center justify-center">
                                    <span class="text-gray-500">🙂</span>
                                </div>
                                <div class="min-w-0">
                                    <div class="font-semibold text-gray-900 truncate">
                                        {{ req.follower && req.follower.name ? req.follower.name : 'Unknown user' }}
                                    </div>
                                    <div class="text-xs text-gray-500 truncate">
                                        {{ req.follower && req.follower.email ? req.follower.email : '' }}
                                    </div>
                                </div>
                            </div>
                            <div class="flex items-center gap-2">
                                <button @click="respondRequest(req.id, 'ACCEPT')"
                                    class="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold">
                                    Confirm
                                </button>
                                <button @click="respondRequest(req.id, 'REJECT')"
                                    class="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-800 text-sm font-semibold">
                                    Delete
                                </button>
                            </div>
                        </div>

                        <div v-if="isLoadingRequests && followRequests && followRequests.length > 0"
                            class="p-4 text-sm text-gray-500">
                            Loading more…
                        </div>

                        <div v-if="!isLoadingRequests && !requestsHasMore && followRequests && followRequests.length > 0"
                            class="p-4 text-xs text-gray-400 text-center">
                            You’ve reached the end.
                        </div>

                        <div v-if="!isLoadingRequests && (!followRequests || followRequests.length === 0)"
                            class="p-4 text-sm text-gray-500">
                            No pending requests.
                        </div>
                    </div>
                </div>
            </div>
        </base-modal>
    </div>
</template>

<script>
import ThinkTank from "./components/ThinkTank.vue";
import Engagement from "./components/Engagement.vue";
import StudentLife from "./components/StudentLife.vue";
import ChangePassword from "./components/ChangePassword.vue";
import ProfileAbout from "./components/ProfileAbout.vue";
import ProfilePosts from "./components/ProfilePosts.vue";
import gql from 'graphql-tag';
import { fileSecurityValidator } from '../../helpers/fileSecurity';
const UPDATE_PROFILE_PICTURE = gql`
  mutation UpdateProfilePicture($file: Upload!) {
    updateProfilePicture(profile_picture: $file) { error error_code message data { profile_picture_url } }
  }
`;
const UPDATE_PROFILE_DETAILS = gql`
  mutation UpdateProfileDetails($input: UpdateProfileDetailsInput!) {
    updateProfileDetails(input: $input) { error error_code message }
  }
`;
const GET_PROFILE = gql`
  query GetProfile($token: String) {
    getProfile(token: $token) {
      error
      error_code
      message
      data {
        id
        token
        name
        email
        position
        created_at
        profile_picture_url
        followers_count
        following_count
        viewer_follow_status
        userDetails {
            bio
            is_private
            detailed_description
            website
            linkedin
            instagram
            facebook
            snapchat
            twitter
            youtube
        }
        phone_number
        entity
        campuses
      }
    }
  }
`;

const FOLLOW_USER = gql`
  mutation FollowUser($userToken: String!) {
    followUser(userToken: $userToken) {
      error
      error_code
      message
      data { status }
    }
  }
`;

const UNFOLLOW_USER = gql`
  mutation UnfollowUser($userToken: String!) {
    unfollowUser(userToken: $userToken) { error error_code message }
  }
`;

const REMOVE_FOLLOWER = gql`
  mutation RemoveFollower($userToken: String!) {
    removeFollower(userToken: $userToken) { error error_code message }
  }
`;

const PENDING_FOLLOW_REQUESTS_COUNT = gql`
  query PendingFollowRequestsCount {
    pendingFollowRequestsCount
  }
`;

const PENDING_FOLLOW_REQUESTS = gql`
  query PendingFollowRequests($first: Int, $page: Int) {
    pendingFollowRequests(first: $first, page: $page) {
      error
      error_code
      message
      paginatorInfo { currentPage lastPage hasMorePages total }
      data {
        id
        status
        created_at
        follower { id token name email profile_picture_url }
      }
    }
  }
`;

const RESPOND_FOLLOW_REQUEST = gql`
  mutation RespondToFollowRequest($requestId: ID!, $action: String!) {
    respondToFollowRequest(requestId: $requestId, action: $action) {
      error
      error_code
      message
      data { status }
    }
  }
`;

const GET_FOLLOWERS = gql`
  query Followers($userToken: String, $search: String, $first: Int, $page: Int) {
    followers(userToken: $userToken, search: $search, first: $first, page: $page) {
      error
      error_code
      message
      paginatorInfo { currentPage hasMorePages lastPage total }
      data { id token name email profile_picture_url viewer_follow_status }
    }
  }
`;

const GET_FOLLOWING = gql`
  query Following($userToken: String, $search: String, $first: Int, $page: Int) {
    following(userToken: $userToken, search: $search, first: $first, page: $page) {
      error
      error_code
      message
      paginatorInfo { currentPage hasMorePages lastPage total }
      data { id token name email profile_picture_url viewer_follow_status }
    }
  }
`;
export default {
    name: "my-profile-new-ui-index",
    props: {
        token: {
            type: String,
            default: ""
        }
    },
    created() {
        this.getProfile();
    },
    watch: {
        '$route.params.token': function () {
            // Vue reuses component for /profile <-> /profile/:token, so refetch on param change.
            this.selectedTab = 'posts';
            this.error = null;
            this.getProfile();
        },
        token: function () {
            // Also refetch if parent prop changes.
            this.selectedTab = 'posts';
            this.error = null;
            this.getProfile();
        }
    },
    mounted() { },
    beforeUnmount() { },
    components: {
        ThinkTank,
        Engagement,
        StudentLife,
        ChangePassword,
        ProfileAbout,
        ProfilePosts,
    },
    data() {
        return {
            tabs: [
                { key: "posts", label: "Posts", icon: "💬" },
                { key: "about", label: "About", icon: "📝" },
                { key: "think-tank", label: "Think Tank", icon: "💡" },
                { key: "engagement", label: "Engagement", icon: "🤝" },
                { key: "student-life", label: "Student Life", icon: "🎓" },
                { key: "change-password", label: "Change Password", icon: "🔒" }
            ],
            selectedTab: "posts",
            profile: {
                id: "",
                url: "",
                name: "",
                email: "",
                position: "",
                bio: "",
                isPrivate: false,
                website: "",
                linkedin: "",
                instagram: "",
                facebook: "",
                snapchat: "",
                twitter: "",
                youtube: "",
                phone: "",
                entity: "",
                campuses: "",
                createdAt: "",
                detailedDescription: "",
                followersCount: 0,
                followingCount: 0,
                viewerFollowStatus: "NONE"
            }
            , isLoadingProfile: true
            , error: null
            , showEditProfileModal: false
            , isFollowBusy: false
            , postsRefreshKey: 0
            , followRequestsCount: 0
            , showFollowRequestsModal: false
            , followRequests: []
            , isLoadingRequests: false
            , requestsPage: 1
            , requestsHasMore: false
            , showFollowListModal: false
            , followListMode: 'followers' // followers|following
            , followListTitle: ''
            , followListItems: []
            , followListPage: 1
            , followListHasMore: false
            , isLoadingFollowList: false
            , followListLoadError: null
            , followListSearch: ''
            , followListSearchDebounce: null
            , followActionBusyByToken: {}
            , editForm: {
                phone_number: "",
                bio: "",
                is_private: false,
                website: "",
                linkedin: "",
                instagram: "",
                facebook: "",
                snapchat: "",
                twitter: "",
                youtube: ""
            }
        };
    },
    computed: {
        signedInUserId() {
            return this.$store.getters.userId;
        },
        isOwnProfile() {
            const noToken = !this.$route.params.token && !this.token;
            const sameUser = this.profile && this.profile.id && this.signedInUserId && String(this.profile.id) === String(this.signedInUserId);
            return noToken || sameUser;
        },
        displayEntity() {
            if (!this.profile || this.profile.entity === null) return 'All Entity';
            const raw = this.profile.entity || '';
            if (!raw) return 'All Entity';
            const first = raw.split(',')[0];
            const val = first && first.trim() ? first.trim() : '';
            if (!val) return 'All Entity';
            return val === 'ATHS' ? 'ATS' : val;
        },
        displayCampus() {
            if (!this.profile) return 'All Campus';
            const raw = this.profile.campuses || '';
            if (!raw) return 'All Campus';
            const first = raw.split(',')[0];
            return first && first.trim() ? first.trim() : 'All Campus';
        },
        displayedTabs() {
            var result = [];
            for (var i = 0; i < this.tabs.length; i++) {
                var t = this.tabs[i];
                if (t.key === 'change-password' && !this.isOwnProfile) { continue; }
                result.push(t);
            }
            return result;
        }
    },
    methods: {
        bumpPostsRefresh() {
            this.postsRefreshKey = this.postsRefreshKey + 1;
        },
        openFollowersModal() {
            this.followListMode = 'followers';
            this.followListTitle = 'Followers';
            this.openFollowListModal();
        },
        openFollowingModal() {
            this.followListMode = 'following';
            this.followListTitle = 'Following';
            this.openFollowListModal();
        },
        openFollowListModal() {
            this.showFollowListModal = true;
            this.followListItems = [];
            this.followListPage = 1;
            this.followListHasMore = true;
            this.followListLoadError = null;
            this.followListSearch = '';
            this.$nextTick(() => {
                this.loadMoreFollowList();
            });
        },
        closeFollowListModal() {
            this.showFollowListModal = false;
        },
        loadMoreFollowList() {
            if (!this.showFollowListModal) return;
            if (this.isLoadingFollowList) return;
            if (!this.followListHasMore) return;
            if (!this.profile || !this.profile.token) return;

            this.isLoadingFollowList = true;
            this.followListLoadError = null;

            var q = this.followListMode === 'following' ? GET_FOLLOWING : GET_FOLLOWERS;

            this.$apollo.query({
                query: q,
                variables: { userToken: this.profile.token, search: this.followListSearch, first: 10, page: this.followListPage },
                fetchPolicy: 'network-only'
            }).then((res) => {
                var payload = null;
                if (this.followListMode === 'following') {
                    payload = res && res.data && res.data.following;
                } else {
                    payload = res && res.data && res.data.followers;
                }

                if (payload && payload.error) {
                    this.followListLoadError = payload.message || 'Unable to load list.';
                    this.followListHasMore = false;
                    return;
                }

                var items = payload && payload.data ? payload.data : [];
                for (var i = 0; i < items.length; i++) {
                    this.followListItems.push(items[i]);
                }

                var pi = payload && payload.paginatorInfo ? payload.paginatorInfo : null;
                this.followListHasMore = !!(pi && pi.hasMorePages);
                if (this.followListHasMore) {
                    this.followListPage = this.followListPage + 1;
                }
            }).catch((e) => {
                this.followListLoadError = 'Unable to load list.';
                console.error('Follow list query failed', e);
            }).finally(() => {
                this.isLoadingFollowList = false;
            });
        },
        onFollowListSearchInput() {
            if (this.followListSearchDebounce) {
                clearTimeout(this.followListSearchDebounce);
                this.followListSearchDebounce = null;
            }
            this.followListSearchDebounce = setTimeout(() => {
                if (!this.showFollowListModal) return;
                this.followListItems = [];
                this.followListPage = 1;
                this.followListHasMore = true;
                this.followListLoadError = null;
                this.loadMoreFollowList();
            }, 350);
        },
        isBusyForToken(token) {
            return !!(token && this.followActionBusyByToken && this.followActionBusyByToken[token]);
        },
        setBusyForToken(token, val) {
            if (!token) return;
            if (!this.followActionBusyByToken) this.followActionBusyByToken = {};
            this.followActionBusyByToken[token] = !!val;
        },
        updateFollowListUser(token, nextFields) {
            if (!token) return;
            if (!this.followListItems || this.followListItems.length === 0) return;
            var out = [];
            for (var i = 0; i < this.followListItems.length; i++) {
                var item = this.followListItems[i];
                if (item && item.token && String(item.token) === String(token)) {
                    out.push(Object.assign({}, item, nextFields || {}));
                } else {
                    out.push(item);
                }
            }
            this.followListItems = out;
        },
        removeFollowListUser(token) {
            if (!token) return;
            if (!this.followListItems || this.followListItems.length === 0) return;
            var out = [];
            for (var i = 0; i < this.followListItems.length; i++) {
                var item = this.followListItems[i];
                if (item && item.token && String(item.token) === String(token)) {
                    continue;
                }
                out.push(item);
            }
            this.followListItems = out;
        },
        listFollowLabel(u) {
            if (!u) return 'Follow';
            if (u.viewer_follow_status === 'FOLLOWING') return 'Following';
            if (u.viewer_follow_status === 'REQUESTED') return 'Requested';
            if (u.viewer_follow_status === 'SELF') return 'You';
            return 'Follow';
        },
        onClickListFollow(u) {
            if (!u || !u.token) return;
            if (u.viewer_follow_status === 'SELF') return;
            if (this.isBusyForToken(u.token)) return;

            this.setBusyForToken(u.token, true);

            var isUnfollow = u.viewer_follow_status === 'FOLLOWING' || u.viewer_follow_status === 'REQUESTED';
            if (isUnfollow) {
                this.$apollo.mutate({
                    mutation: UNFOLLOW_USER,
                    variables: { userToken: u.token },
                    errorPolicy: 'all'
                }).then((res) => {
                    var payload = res && res.data && res.data.unfollowUser;
                    if (payload && payload.error) {
                        this.$swal && this.$swal.fire({ icon: 'error', title: 'Action failed', text: payload.message || 'Please try again.' });
                        return;
                    }
                    // If I'm viewing my own "Following" list, unfollow should remove the user from the list.
                    if (this.isOwnProfile && this.followListMode === 'following') {
                        this.removeFollowListUser(u.token);
                    } else {
                        this.updateFollowListUser(u.token, { viewer_follow_status: 'NONE' });
                    }
                    if (!this.isOwnProfile) {
                        this.bumpPostsRefresh();
                    }
                    this.getProfile();
                }).catch((e) => {
                    var msg = (e && e.message) ? e.message : 'Please try again.';
                    this.$swal && this.$swal.fire({ icon: 'error', title: 'Action failed', text: msg });
                }).finally(() => {
                    this.setBusyForToken(u.token, false);
                });
                return;
            }

            this.$apollo.mutate({
                mutation: FOLLOW_USER,
                variables: { userToken: u.token }
            }).then((res) => {
                var payload = res && res.data && res.data.followUser;
                if (payload && payload.error) {
                    this.$swal && this.$swal.fire({ icon: 'error', title: 'Action failed', text: payload.message || 'Please try again.' });
                    return;
                }
                var s = payload && payload.data && payload.data.status ? payload.data.status : '';
                if (s === 'ACCEPTED') {
                    this.updateFollowListUser(u.token, { viewer_follow_status: 'FOLLOWING' });
                } else if (s === 'PENDING') {
                    this.updateFollowListUser(u.token, { viewer_follow_status: 'REQUESTED' });
                }
                if (!this.isOwnProfile) {
                    this.bumpPostsRefresh();
                }
                this.getProfile();
            }).catch(() => {
                this.$swal && this.$swal.fire({ icon: 'error', title: 'Action failed', text: 'Please try again.' });
            }).finally(() => {
                this.setBusyForToken(u.token, false);
            });
        },
        onRemoveFollower(u) {
            if (!this.isOwnProfile) return;
            if (!u || !u.token) return;
            if (this.isBusyForToken(u.token)) return;

            this.setBusyForToken(u.token, true);
            this.$apollo.mutate({
                mutation: REMOVE_FOLLOWER,
                variables: { userToken: u.token }
            }).then((res) => {
                var payload = res && res.data && res.data.removeFollower;
                if (payload && payload.error) {
                    this.$swal && this.$swal.fire({ icon: 'error', title: 'Action failed', text: payload.message || 'Please try again.' });
                    return;
                }
                // remove from list locally
                var next = [];
                for (var i = 0; i < this.followListItems.length; i++) {
                    if (String(this.followListItems[i].id) !== String(u.id)) {
                        next.push(this.followListItems[i]);
                    }
                }
                this.followListItems = next;
                this.getProfile();
            }).catch(() => {
                this.$swal && this.$swal.fire({ icon: 'error', title: 'Action failed', text: 'Please try again.' });
            }).finally(() => {
                this.setBusyForToken(u.token, false);
            });
        },
        onFollowListScroll(e) {
            var el = e && e.target ? e.target : null;
            if (!el) return;
            // load next page when within 120px from bottom
            if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) {
                this.loadMoreFollowList();
            }
        },

        openEditModal() {
            this.editForm.phone_number = this.profile.phone || '';
            this.editForm.bio = this.profile.bio || '';
            this.editForm.is_private = !!this.profile.isPrivate;
            this.editForm.website = this.profile.website || '';
            this.editForm.linkedin = this.profile.linkedin || '';
            this.editForm.instagram = this.profile.instagram || '';
            this.editForm.facebook = this.profile.facebook || '';
            this.editForm.snapchat = this.profile.snapchat || '';
            this.editForm.twitter = this.profile.twitter || '';
            this.editForm.youtube = this.profile.youtube || '';
            this.showEditProfileModal = true;
        },
        closeEditModal() { this.showEditProfileModal = false; },
        submitEditProfile() {
            var input = {};
            for (var k in this.editForm) { if (Object.prototype.hasOwnProperty.call(this.editForm, k)) { input[k] = this.editForm[k]; } }
            this.$apollo.mutate({
                mutation: UPDATE_PROFILE_DETAILS,
                variables: { input }
            }).then((res) => {
                var payload = res && res.data && res.data.updateProfileDetails;
                if (payload && payload.error) {
                    this.$swal && this.$swal.fire({ icon: 'error', title: 'Update failed', text: payload.message || 'Please try again.' });
                    return;
                }
                this.$swal && this.$swal.fire({
                    icon: 'success',
                    title: (payload && payload.message) || 'Profile updated successfully',
                    toast: true,
                    position: 'top-end',
                    timer: 2500,
                    showConfirmButton: false
                });
                this.closeEditModal();
                this.getProfile();
            }).catch(() => {
                this.$swal && this.$swal.fire({ icon: 'error', title: 'Update failed', text: 'Please try again.' });
            });
        },
        onTogglePrivacy() {
            if (!this.isOwnProfile) return;
            var input = { is_private: !this.profile.isPrivate };
            this.$apollo.mutate({
                mutation: UPDATE_PROFILE_DETAILS,
                variables: { input }
            }).then((res) => {
                var payload = res && res.data && res.data.updateProfileDetails;
                if (payload && payload.error) {
                    this.$swal && this.$swal.fire({ icon: 'error', title: 'Update failed', text: payload.message || 'Please try again.' });
                    return;
                }
                this.getProfile();
            }).catch(() => {
                this.$swal && this.$swal.fire({ icon: 'error', title: 'Update failed', text: 'Please try again.' });
            });
        },
        loadFollowRequestsCount() {
            if (!this.isOwnProfile) return;
            this.$apollo.query({
                query: PENDING_FOLLOW_REQUESTS_COUNT,
                fetchPolicy: 'network-only'
            }).then((res) => {
                var c = res && res.data ? res.data.pendingFollowRequestsCount : 0;
                this.followRequestsCount = c || 0;
            }).catch(() => {
                // Silent fail: tile will show 0 until refreshed.
                this.followRequestsCount = 0;
            });
        },
        openFollowRequestsModal() {
            if (!this.isOwnProfile) return;
            this.showFollowRequestsModal = true;
            this.followRequests = [];
            this.requestsPage = 1;
            this.requestsHasMore = true;
            this.loadMoreFollowRequests();
        },
        closeFollowRequestsModal() {
            this.showFollowRequestsModal = false;
        },
        loadMoreFollowRequests() {
            if (!this.showFollowRequestsModal) return;
            if (!this.isOwnProfile) return;
            if (this.isLoadingRequests) return;
            if (!this.requestsHasMore) return;

            this.isLoadingRequests = true;
            this.$apollo.query({
                query: PENDING_FOLLOW_REQUESTS,
                variables: { first: 10, page: this.requestsPage },
                fetchPolicy: 'network-only'
            }).then((res) => {
                var payload = res && res.data && res.data.pendingFollowRequests;
                if (payload && payload.error) {
                    this.$swal && this.$swal.fire({ icon: 'error', title: 'Unable to load requests', text: payload.message || 'Please try again.' });
                    this.requestsHasMore = false;
                    return;
                }
                var items = payload && payload.data ? payload.data : [];
                for (var i = 0; i < items.length; i++) {
                    this.followRequests.push(items[i]);
                }
                var pi = payload && payload.paginatorInfo ? payload.paginatorInfo : null;
                this.requestsHasMore = !!(pi && pi.hasMorePages);
                if (this.requestsHasMore) {
                    this.requestsPage = this.requestsPage + 1;
                }
                // keep count synced
                if (pi && typeof pi.total === 'number') {
                    this.followRequestsCount = pi.total;
                }
            }).catch(() => {
                this.$swal && this.$swal.fire({ icon: 'error', title: 'Unable to load requests', text: 'Please try again.' });
                this.requestsHasMore = false;
            }).finally(() => {
                this.isLoadingRequests = false;
            });
        },
        onFollowRequestsScroll(e) {
            var el = e && e.target ? e.target : null;
            if (!el) return;
            if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) {
                this.loadMoreFollowRequests();
            }
        },
        followActionLabel() {
            if (this.isOwnProfile) return '';
            if (this.profile.viewerFollowStatus === 'FOLLOWING') return 'Following';
            if (this.profile.viewerFollowStatus === 'REQUESTED') return 'Requested';
            return 'Follow';
        },
        onClickFollow() {
            if (this.isOwnProfile) return;
            if (!this.profile || !this.profile.token) return;
            if (this.isFollowBusy) return;

            this.isFollowBusy = true;

            // If following or requested -> unfollow/cancel (same backend delete)
            if (this.profile.viewerFollowStatus === 'FOLLOWING' || this.profile.viewerFollowStatus === 'REQUESTED') {
                this.$apollo.mutate({
                    mutation: UNFOLLOW_USER,
                    variables: { userToken: this.profile.token },
                    errorPolicy: 'all'
                }).then((res) => {
                    var payload = res && res.data && res.data.unfollowUser;
                    if (payload && payload.error) {
                        this.$swal && this.$swal.fire({ icon: 'error', title: 'Action failed', text: payload.message || 'Please try again.' });
                        return;
                    }
                    this.bumpPostsRefresh();
                    this.getProfile();
                }).catch((e) => {
                    var msg = (e && e.message) ? e.message : 'Please try again.';
                    this.$swal && this.$swal.fire({ icon: 'error', title: 'Action failed', text: msg });
                }).finally(() => { this.isFollowBusy = false; });
                return;
            }

            this.$apollo.mutate({
                mutation: FOLLOW_USER,
                variables: { userToken: this.profile.token }
            }).then((res) => {
                var payload = res && res.data && res.data.followUser;
                if (payload && payload.error) {
                    this.$swal && this.$swal.fire({ icon: 'error', title: 'Action failed', text: payload.message || 'Please try again.' });
                    return;
                }
                var s = payload && payload.data && payload.data.status ? payload.data.status : '';
                if (s === 'ACCEPTED') {
                    this.profile.viewerFollowStatus = 'FOLLOWING';
                } else if (s === 'PENDING') {
                    this.profile.viewerFollowStatus = 'REQUESTED';
                }
                this.bumpPostsRefresh();
                this.getProfile();
            }).catch(() => {
                this.$swal && this.$swal.fire({ icon: 'error', title: 'Action failed', text: 'Please try again.' });
            }).finally(() => { this.isFollowBusy = false; });
        },
        // loadFollowRequests() removed: replaced by Follow Requests modal infinite loader
        respondRequest(requestId, action) {
            if (!this.isOwnProfile) return;
            this.$apollo.mutate({
                mutation: RESPOND_FOLLOW_REQUEST,
                variables: { requestId: requestId, action: action }
            }).then((res) => {
                var payload = res && res.data && res.data.respondToFollowRequest;
                if (payload && payload.error) {
                    this.$swal && this.$swal.fire({ icon: 'error', title: 'Action failed', text: payload.message || 'Please try again.' });
                    return;
                }
                // Refresh modal list + tile count
                this.followRequests = [];
                this.requestsPage = 1;
                this.requestsHasMore = true;
                this.loadMoreFollowRequests();
                this.loadFollowRequestsCount();
                this.getProfile();
            }).catch(() => {
                this.$swal && this.$swal.fire({ icon: 'error', title: 'Action failed', text: 'Please try again.' });
            });
        },
        copyProfileUrl() {
            if (!this.profile || !this.profile.url) return;
            try {
                navigator.clipboard.writeText(this.profile.url);
                this.$swal && this.$swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: 'Profile link copied',
                    showConfirmButton: false,
                    timer: 1500
                });
            } catch (e) {
                const el = document.createElement('textarea');
                el.value = this.profile.url;
                document.body.appendChild(el);
                el.select();
                document.execCommand('copy');
                document.body.removeChild(el);
            }
        },
        async onProfilePictureChange(e) {
            const file = e.target && e.target.files && e.target.files[0];
            if (!file) return;

            // Frontend validation aligned with tab-1.vue (images_only, 2MB)
            try {
                const validation = await fileSecurityValidator.validateFileWithSize(file, 'images_only', 2);
                if (!validation.valid) {
                    this.$swal && this.$swal.fire({
                        icon: 'error',
                        title: `Security Alert${validation.attackType ? `: ${validation.attackType}` : ''}`,
                        html: `<div class="text-left">
                                <p><strong>File Rejected:</strong> ${validation.message}</p>
                                <p class="text-sm text-gray-600 mt-2">
                                    <strong>File:</strong> ${file.name}<br>
                                    <strong>Size:</strong> ${fileSecurityValidator.formatFileSize(file.size)}
                                </p>
                              </div>`,
                        confirmButtonText: 'OK',
                        confirmButtonColor: '#dc2626'
                    });
                    e.target.value = '';
                    return;
                }
            } catch (err) {
                console.error('File validation error:', err);
                this.$swal && this.$swal.fire({ icon: 'error', title: 'Validation Error', text: 'Unable to validate file. Please try again.' });
                e.target.value = '';
                return;
            }

            this.$apollo.mutate({
                mutation: UPDATE_PROFILE_PICTURE,
                variables: { file }
            }).then((res) => {
                const payload = res && res.data && res.data.updateProfilePicture;
                if (payload && payload.error) {
                    this.$swal && this.$swal.fire({ icon: 'error', title: 'Upload failed', text: payload.message || 'Please try again.' });
                    return;
                }

                this.$store.commit("setProfilePictureUrl", {
                    profilePictureUrl: payload.data.profile_picture_url,
                });

                this.$swal && this.$swal.fire({
                    icon: 'success',
                    title: (payload && payload.message) || 'Profile picture updated successfully',
                    toast: true,
                    position: 'top-end',
                    timer: 2500,
                    showConfirmButton: false
                });
                // Refresh profile to get latest image
                this.getProfile();
            }).catch(() => {
                this.$swal && this.$swal.fire({ icon: 'error', title: 'Upload failed', text: 'Please try again.' });
            }).finally(() => {
                e.target.value = '';
            });
        },
        getProfile() {
            var tokenVal = this.$route.params.token || this.token || null;
            this.isLoadingProfile = true;
            this.error = null;
            this.$apollo.query({
                query: GET_PROFILE,
                variables: { token: tokenVal },
                fetchPolicy: 'network-only'
            }).then((res) => {
                try {
                    var payload = res && res.data && res.data.getProfile;
                    if (payload && !payload.error && payload.data) {
                        var u = payload.data;
                        this.profile.id = u.id || '';
                        this.profile.token = u.token || '';
                        this.profile.url = process.env.VUE_APP_FRONTEND_URL + 'profile/' + u.token;
                        this.profile.name = u.name || '';
                        this.profile.email = u.email || '';
                        this.profile.position = u.position || '';
                        this.profile.bio = (u.userDetails && u.userDetails.bio) || '';
                        this.profile.isPrivate = !!(u.userDetails && u.userDetails.is_private);
                        this.profile.website = (u.userDetails && u.userDetails.website) || '';
                        this.profile.linkedin = (u.userDetails && u.userDetails.linkedin) || '';
                        this.profile.instagram = (u.userDetails && u.userDetails.instagram) || '';
                        this.profile.facebook = (u.userDetails && u.userDetails.facebook) || '';
                        this.profile.snapchat = (u.userDetails && u.userDetails.snapchat) || '';
                        this.profile.twitter = (u.userDetails && u.userDetails.twitter) || '';
                        this.profile.youtube = (u.userDetails && u.userDetails.youtube) || '';
                        this.profile.createdAt = u.created_at || '';
                        this.profile.profilePictureUrl = u.profile_picture_url || '';
                        this.profile.phone = u.phone_number || '';
                        this.profile.entity = u.entity || '';
                        this.profile.campuses = u.campuses || '';
                        var ud = u.userDetails;
                        this.profile.detailedDescription = (ud && ud.detailed_description) ? ud.detailed_description : '';
                        this.profile.followersCount = u.followers_count || 0;
                        this.profile.followingCount = u.following_count || 0;
                        this.profile.viewerFollowStatus = u.viewer_follow_status || 'NONE';

                        if (this.isOwnProfile) {
                            this.loadFollowRequestsCount();
                        } else {
                            this.followRequestsCount = 0;
                        }
                    } else {
                        this.error = 'This profile link is invalid or the user no longer exists.';
                    }
                } catch (e) {
                    console.error('Failed to parse GetProfile response', e);
                    this.error = 'Unable to load profile at the moment. Please try again later.';
                }
            }).catch((e) => {
                console.error('GetProfile query failed', e);
                this.error = 'Unable to load profile at the moment. Please try again later.';
            }).finally(() => {
                this.isLoadingProfile = false;
            });
        },
        selectTab(key) {
            this.selectedTab = key;
        },
        formatMemberSince(dateString) {
            if (!dateString) return '—';
            try {
                const d = new Date(dateString);
                return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
            } catch (e) {
                return '—';
            }
        },
        getProfilePicture(url) {
            if (!url) return this.getDefaultAvatar();
            if (url.indexOf('http') === 0) return url;
            return process.env.VUE_APP_PUBLIC_URL + url;
        },
        normalizeUrl(url) {
            if (!url) return '';
            // If it already looks like an absolute URL, return as-is
            if (url.indexOf('http://') === 0 || url.indexOf('https://') === 0) return url;
            // Otherwise, prefix with https
            return 'https://' + url.replace(/^\/+/, '');
        },
        getDefaultAvatar() {
            return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNlNWU3ZWYiLz4KPHBhdGggZD0iTTIwIDEwQzIyLjA3NjEgMTAgMjQgMTIuMDIzOSAyNCAxNEMyNCAxNS45NzYxIDIyLjA3NjEgMTggMjAgMThDMTcuOTIzOSAxOCAxNiAxNS45NzYxIDE2IDE0QzE2IDEyLjAyMzkgMTcuOTIzOSAxMCAyMCAxMFoiIGZpbGw9IiM5Y2EzYWYiLz4KPHBhdGggZD0iTTI4IDI4QzI4IDI0LjY4NjMgMjQuNDE4MyAyMiAyMCAyMkMxNS41ODE3IDIyIDEyIDI0LjY4NjMgMTIgMjhIMjhaIiBmaWxsPSIjOWNhM2FmIi8+Cjwvc3ZnPgo=';
        },
        handleImageError(event) {
            event.target.src = this.getDefaultAvatar();
        },
        loadMore() {
            var remaining = this.projects.length - this.itemsToShow;
            this.itemsToShow += remaining > 6 ? 6 : remaining;
        }
    }
}
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
    transition: all 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    transform: translateY(6px);
}

.rotate-90 {
    transform: rotate(90deg);
}
</style>